import { useMemo } from "react";
import { useLiveQuery } from "@tanstack/react-db";
import { eq, rolesCollection } from "@cliqsuite/db";
import { useAuth } from "./auth";

export type OwnerPermission = "Operations & menu" | "Inventory & staff" | "View reports" | "Payroll" | "User management" | "Billing & plan" | "Delete data";

const OWNER_PERMISSIONS: OwnerPermission[] = ["Operations & menu", "Inventory & staff", "View reports", "Payroll", "User management", "Billing & plan", "Delete data"];

/** Resolves permissions from the signed-in user's live role definition. Role
 * names are presentation; the permission list is the actual authority. */
export function usePermissions() {
  const { user } = useAuth();
  const { data, isReady } = useLiveQuery((q) => user ? q.from({ r: rolesCollection }).where(({ r }) => eq(r.business_id, user.business_id)) : q.from({ r: rolesCollection }).where(({ r }) => eq(r.id, "__none__")), [user?.business_id]);
  // Existing businesses may predate the roles seed. The API still treats the
  // business owner as full-access, so missing presentation data must not hide
  // owner-only controls in the client.
  const permissions = useMemo(() => new Set(
    user?.role.toLowerCase() === "owner"
      ? OWNER_PERMISSIONS
      : data?.find((role) => role.name.toLowerCase() === user?.role.toLowerCase())?.permissions ?? [],
  ), [data, user?.role]);
  return { permissions, isReady, can: (permission?: OwnerPermission) => !permission || permissions.has(permission) };
}
